import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@tinct/web-components';
import { cn } from '@tinct/core-utils';
import { CrmSegment, ImportResponse, ReadCompanyRef } from '@tinct/core-api-models';
import {
  useBuyerImportJobAlert,
  useImportBuyerActions,
  useImportBuyerModal,
} from './hooks';
import { AddBuyersStep, ImportingStep, SuccessStep } from './steps';
import { MappingStep } from '../create-audience-modal/steps';

type ImportBuyerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (importResult: ImportResponse) => void;
  /** Where the job-alert widget link should send the user once the import
   *  job completes. */
  completedUrl: string;
};

export const ImportBuyerModal = ({
  open,
  onOpenChange,
  onSuccess,
  completedUrl,
}: ImportBuyerModalProps) => {
  const {
    step,
    analysisResult,
    fileBase64,
    mapping,
    setMapping,
    importResult,
    setImportResult,
    goToMapping,
    goToImporting,
    goToSuccess,
    goBack,
    reset,
  } = useImportBuyerModal();

  const { analyzeAction, importAction } = useImportBuyerActions();

  const navigate = useNavigate();
  const location = useLocation();
  const { start: startJobAlert } = useBuyerImportJobAlert({
    jobId: importResult?.jobId,
    completedUrl,
  });

  // Register the import job in the global job-alert store as soon as we know
  // its id. Idempotent — re-registering merges with the existing entry.
  useEffect(() => {
    if (importResult?.jobId) startJobAlert(importResult.jobId);
  }, [importResult?.jobId, startJobAlert]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  // CSV file selection — analyze, then move to column mapping.
  const handleCsvFileSelect = useCallback(
    async (file: File | null) => {
      if (!file) return;
      const result = await analyzeAction.analyzeCsv({ file });
      if (result) goToMapping(result.analysis, result.base64);
    },
    [analyzeAction, goToMapping],
  );

  // Capture the server-returned import response so the importing step can poll
  // job progression. If the server completed synchronously (no jobId), jump
  // straight to success.
  const handleImportResponse = useCallback(
    (response: ImportResponse | undefined) => {
      if (!response) {
        goBack();
        return;
      }
      setImportResult(response);
      if (!response.jobId) goToSuccess(response);
    },
    [setImportResult, goToSuccess, goBack],
  );

  const handleImportCsv = useCallback(async () => {
    if (!fileBase64) return;
    goToImporting();
    const response = await importAction.importCsv({ fileBase64, mapping });
    handleImportResponse(response);
  }, [fileBase64, mapping, importAction, goToImporting, handleImportResponse]);

  const handleImportCompanies = useCallback(
    async (companies: ReadCompanyRef[]) => {
      goToImporting();
      const response = await importAction.importCompanies({ companies });
      handleImportResponse(response);
    },
    [importAction, goToImporting, handleImportResponse],
  );

  const handleImportCrmSegment = useCallback(
    async (segment: CrmSegment, integrationId: string) => {
      goToImporting();
      const response = await importAction.importCrmSegment({
        integrationId,
        segment,
      });
      handleImportResponse(response);
    },
    [importAction, goToImporting, handleImportResponse],
  );

  const handleCancel = () => {
    handleOpenChange(false);
  };

  // "Run in background" — close the modal and keep the user on the current page.
  // The job stays tracked via the global job-alert store, which shows progress
  // in the top alert bar / floating widget.
  const handleRunInBackground = () => {
    reset();
    onOpenChange(false);
    navigate(`${location.pathname}${location.search}${location.hash}`);
  };

  const handleDone = () => {
    if (!importResult) return;
    reset();
    onOpenChange(false);
    onSuccess?.(importResult);
  };

  const isOverlayStep = step === 'importing' || step === 'success';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn({
          'sm:max-w-[425px] rounded-3xl p-8': isOverlayStep,
          'sm:max-w-[740px] rounded-2xl p-8': !isOverlayStep,
          'p-0 border-0 bg-transparent shadow-none': isOverlayStep,
        })}
      >
        {step === 'add' && (
          <AddBuyersStep
            isAnalyzing={analyzeAction.isAnalyzing}
            onCsvFileSelect={handleCsvFileSelect}
            onImportCompanies={handleImportCompanies}
            onImportCrmSegment={handleImportCrmSegment}
            onCancel={handleCancel}
          />
        )}

        {step === 'mapping' && analysisResult && (
          <MappingStep
            analysisResult={analysisResult}
            mapping={mapping}
            onMappingChange={setMapping}
            onBack={goBack}
            onContinue={handleImportCsv}
          />
        )}

        {step === 'importing' && (
          <ImportingStep
            jobId={importResult?.jobId ?? null}
            onJobCompleted={() => {
              if (importResult) goToSuccess(importResult);
            }}
            onRunInBackground={handleRunInBackground}
          />
        )}

        {step === 'success' && importResult && (
          <SuccessStep importResult={importResult} onDone={handleDone} />
        )}
      </DialogContent>
    </Dialog>
  );
};
